import React from "react";
import { useCreateSchemaFieldAsset } from "./dataloader";
import {
  ACCEPTED_FILE_MIME_TYPE,
  ACCEPTED_IMAGE_AND_PDF_MIME_TYPE,
  ACCEPTED_IMAGE_MIME_TYPE,
  MAX_DEFAULT_ASSET_UPLOAD_SIZE_KB
} from "shared/utils/constants";
import { AspectRatioRestrictionType, AssetUploadProps, AssetUploadType, SelectedAssetType } from "./types";
import useDropZoneFileService from "../../dropZoneFileService";
import { DEFAULT_ASSET_CONFIG } from "./constants";
import { isInValidAspectRatio } from "./util";

export default function useAssetUploadService({
  onAssetUpload,
  onAssetUploadError,
  assetConfig = DEFAULT_ASSET_CONFIG,
  aspectRatioRestriction = AspectRatioRestrictionType.WARNING,
  ...props
}: AssetUploadProps) {
  const { data: uploadedAsset, mutateAsync: createSchemaFieldAsset } = useCreateSchemaFieldAsset();

  const createAssetHandler = React.useCallback(
    async (asset, name?) => {
      const fieldAsset = {
        displayOrder: 0,
        type: props.assetType,
        name: name,
        registrationId: props.registrationId,
        registrationParticipantId: props.registrationParticipantId
      };
      const formData = new FormData();
      formData.append(
        "fieldAsset",
        new Blob([JSON.stringify(fieldAsset)], {
          type: "application/json"
        })
      );
      try {
        formData.append("file", asset);
        const createdAsset = await createSchemaFieldAsset(formData);

        onAssetUpload?.({
          ...createdAsset,
          incorrectAspectRatio:
            aspectRatioRestriction === AspectRatioRestrictionType.WARNING &&
            isInValidAspectRatio({
              fileDimension: { width: asset.width, height: asset.height },
              recommendedDimension: assetConfig.dimension
            })
        });
      } catch (e) {
        console.error(e);
      }
    },
    [
      aspectRatioRestriction,
      assetConfig.dimension,
      createSchemaFieldAsset,
      onAssetUpload,
      props.assetType,
      props.registrationId,
      props.registrationParticipantId
    ]
  );

  const uploadSelectedAsset = React.useCallback(
    async (obj: SelectedAssetType) => {
      await createAssetHandler(obj.asset, obj.name);
    },
    [createAssetHandler]
  );

  const acceptedMimeType = React.useMemo(() => {
    switch (props.assetUploadType) {
      case AssetUploadType.IMAGE:
        return ACCEPTED_IMAGE_MIME_TYPE;
      case AssetUploadType.FILE:
        return ACCEPTED_FILE_MIME_TYPE;
      case AssetUploadType.BOTH:
        return ACCEPTED_IMAGE_AND_PDF_MIME_TYPE;
      default:
        return "";
    }
  }, [props.assetUploadType]);

  const maxSizeAllowed = React.useMemo(() => {
    return assetConfig?.maxSize || MAX_DEFAULT_ASSET_UPLOAD_SIZE_KB;
  }, [assetConfig?.maxSize]);

  const onFileSelection = React.useCallback(
    (files: Array<File>) => {
      files?.map(async file => {
        await createAssetHandler(file);
      });
    },
    [createAssetHandler]
  );

  const { dropzoneState } = useDropZoneFileService({
    onFileSelect: onFileSelection,
    onFileSelectError: onAssetUploadError,
    acceptedMimeType: acceptedMimeType,
    maxSizeAllowed: maxSizeAllowed,
    disabled: props.disabled,
    aspectRatioRestriction,
    assetConfig
  });

  return {
    dropzoneState,
    uploadedAsset,
    uploadSelectedAsset
  };
}
